/**
 * Light / dark / system switcher.
 *
 * Three states, not a two-way switch: 'system' has to be reachable, or an
 * operator whose machine flips at sunset is stuck on whichever mode they last
 * picked. The trigger shows what is currently painted; the menu shows what is
 * currently chosen.
 */
import { Monitor, Moon, Sun } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { resolve_theme, use_theme_store, type ThemeChoice } from '@/lib/theme-store';
import { Check } from 'lucide-react';

const OPTIONS: { value: ThemeChoice; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

export function ThemeToggle() {
  const choice = use_theme_store((s) => s.choice);
  const set_choice = use_theme_store((s) => s.set_choice);
  const painted = resolve_theme(choice);
  const Icon = painted === 'dark' ? Moon : Sun;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex h-9 w-9 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`Theme: ${choice}`}
        data-testid="theme-toggle"
      >
        <Icon className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        {OPTIONS.map((o) => (
          <DropdownMenuItem
            key={o.value}
            onSelect={() => set_choice(o.value)}
            data-testid={`theme-${o.value}`}
          >
            <o.icon className="h-4 w-4" />
            {o.label}
            {choice === o.value && <Check className="ml-auto h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
